export interface SourcingPriorityOption {
    id: string;
    name: string;
    description: string;
    iconName: string;
}

export interface ComplianceOption {
    value: string;
    label: string;
    description: string;
}

export const SOURCING_PRIORITY_OPTIONS: SourcingPriorityOption[] = [
    { id: 'quality', name: 'Quality', description: 'Product quality and reliability', iconName: 'quality' },
    { id: 'price', name: 'Price', description: 'Cost competitiveness', iconName: 'price' },
    { id: 'reliability', name: 'Reliability', description: 'Supplier track record', iconName: 'reliability' },
    { id: 'established-company', name: 'Established Company', description: 'Company reputation and stability', iconName: 'established' },
    { id: 'support', name: 'Support', description: 'Customer service and technical support', iconName: 'support' },
    { id: 'returns-warranty', name: 'Returns & Warranty', description: 'Return policy and warranty coverage', iconName: 'warranty' },
];

export const LEAD_TIME_PRESETS = [
    '1-2 weeks',
    '2-4 weeks',
    '4-6 weeks',
    '6-8 weeks',
    '8-12 weeks',
    '12+ weeks',
];

export const COMPLIANCE_OPTIONS: ComplianceOption[] = [
    { value: 'ISO 9001', label: 'ISO 9001', description: 'Quality Management System' },
    { value: 'RoHS', label: 'RoHS', description: 'Restriction of Hazardous Substances' },
    { value: 'REACH', label: 'REACH', description: 'Chemical Safety Regulation (EU)' },
    { value: 'CE Marking', label: 'CE Marking', description: 'European Conformity' },
    { value: 'UL Listed', label: 'UL Listed', description: 'Underwriters Laboratories Safety' },
    { value: 'FDA', label: 'FDA', description: 'Food and Drug Administration' },
    { value: 'AS9100', label: 'AS9100', description: 'Aerospace Quality Management' },
    { value: 'ISO 14001', label: 'ISO 14001', description: 'Environmental Management' },
    { value: 'OHSAS 18001', label: 'OHSAS 18001', description: 'Occupational Health & Safety' },
];

// Backend schemas normalize saved values (uppercase, kebab->snake), so restored
// data never matches the option lists above without mapping back.

const COMPLIANCE_ALIASES: Record<string, string> = {
    CE: 'CE Marking',
    UL: 'UL Listed',
};

export function leadTimeFromBackend(value?: string | null): string {
    if (!value) return '';
    const match = LEAD_TIME_PRESETS.find(
        (preset) => preset.toLowerCase() === value.toLowerCase()
    );
    return match || value;
}

export function prioritiesFromBackend(ids?: unknown[] | null): SourcingPriorityOption[] {
    if (!Array.isArray(ids) || ids.length === 0) {
        return [...SOURCING_PRIORITY_OPTIONS];
    }

    const ranked = ids
        .map((raw) => {
            if (raw && typeof raw === 'object') {
                const candidate = raw as Partial<SourcingPriorityOption>;
                if (candidate.id && candidate.name) {
                    return candidate as SourcingPriorityOption;
                }
            }
            if (typeof raw !== 'string') return undefined;
            const normalizedId = raw.toLowerCase().replace(/_/g, '-');
            return SOURCING_PRIORITY_OPTIONS.find((option) => option.id === normalizedId);
        })
        .filter((option): option is SourcingPriorityOption => Boolean(option));

    const missing = SOURCING_PRIORITY_OPTIONS.filter(
        (option) => !ranked.some((item) => item.id === option.id)
    );

    return [...ranked, ...missing];
}

export function complianceFromBackend(values?: string[] | null): string[] {
    if (!Array.isArray(values)) return [];
    return values.map((value) => {
        const alias = COMPLIANCE_ALIASES[value.toUpperCase()];
        if (alias) return alias;
        const match = COMPLIANCE_OPTIONS.find(
            (option) => option.value.toLowerCase() === value.toLowerCase()
        );
        return match ? match.value : value;
    });
}
